import { logger } from '@adornis/base/logging.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import { LogistikPageCart } from '@chancenlogistik/client/pages/logistik-page-cart.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { g_logistikCart } from '@chancenwerk/logistik/client/globalSubjects.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { g_getMaxOrderQuantity } from '../helpers.js';

/**
 * @element hefte-page-cart
 */
@customElement('hefte-page-cart')
export class HeftePageCart extends LogistikPageCart {
  @state() override _maxOrderQuantity = new RXController(this, g_getMaxOrderQuantity());

  protected override async onContinueClicked() {
    const studentCount = await HefteCartOverviewDialog.showPopup<number>({
      props: { totalItemCount: this._totalProductCount },
    }).catch(err => {
      if (err) logger.error({ err }, 'Fehler beim Anzeigen des Übersichtsdialoges');
      return undefined;
    });
    if (studentCount === undefined) return;
    g_logistikCart.value.studentCount = studentCount;
    g_logistikCart.next(g_logistikCart.value);

    super.onContinueClicked();
  }
}

@customElement('hefte-cart-overview-dialog')
class HefteCartOverviewDialog extends ChromiumDialog<number> {
  @property({ attribute: false }) totalItemCount!: RXController<number>;
  @state() studentCount = new RXController<number | null>(this, null);

  static override get element_name() {
    return 'hefte-cart-overview-dialog';
  }
  override heading?: string | undefined = 'Wichtig';
  override content() {
    if (this.totalItemCount === null || this.totalItemCount === undefined)
      throw new Error('Pass total item count to HefteCartOverviewDialog please');
    return html`
      <cr-flex w100 h100>
        <cr-flex padding="sm">
          <cr-text>
            Danke, dass Sie unsere CHANCENhefte bestellen. Bevor der Bestellvorgang abgeschlossen werden kann, bitten
            wir Sie, anzugeben, wie viele Schüler:innen diese Hefte erhalten sollen. So können wir nachhalten, wie viele
            Schüler:innen wir mit den CHANCENheften tatsächlich erreichen.
          </cr-text>
        </cr-flex>
        <cr-flex padding="sm">
          <cr-text bold>Beispiel</cr-text>
        </cr-flex>
        <cr-flex padding="sm">
          <cr-text>
            Sie haben 25 Englischhefte und 25 Deutschhefte bestellt. Sind diese 50 Hefte für 50 verschiedene
            Schüler:innen? Oder sind die 50 Hefte für 25 Schüler:innen, also 2 Hefte pro Schüler:in?
          </cr-text>
        </cr-flex>
        <cr-flex padding="sm">
          <cr-text>
            ${this.totalItemCount.value > 1
              ? html`Es befinden sich <b>${this.totalItemCount.value} Produkte im Warenkorb</b>`
              : html`Es befindet sich <b>1 Produkt im Warenkorb</b>`}
          </cr-text>
        </cr-flex>
        <cr-flex padding="sm">
          <cr-input
            placeholder="Wie viele Schüler:innen erwarten Sie mit dieser Anzahl an CHANCENheften zu erreichen?"
            type="number"
            ${css({ width: '100%' })}
            .value=${this.studentCount}
            @value-picked=${(event: CustomEvent<{ value: number | null }>) => {
              if (!event.detail.value || event.detail.value <= 0) this.studentCount.next(null);
              else this.studentCount.next(event.detail.value);
            }}
          >
          </cr-input>
        </cr-flex>
      </cr-flex>
    `;
  }

  override footerContent() {
    return html`
      <cr-button
        ?disabled=${(this.studentCount.value ?? 0) <= 0}
        @click=${() => {
          if (!this.studentCount.value || this.studentCount.value <= 0)
            void ChromiumSnackbar.show('Bitte tragen Sie die Anzahl der Schüler:innen ein');
          else this.resolve(this.studentCount.value);
        }}
      >
        Bestellen
      </cr-button>
    `;
  }
}
